import { OrderSide, OrderType } from '@/types/generated/common/trading'
import type { NewOrderParams } from '@/types/generated/execution'

export interface ExitBracketConfig {
  /** Take-profit price (the Exit Condition `priceAbove` field). */
  takeProfit?: string
  /** Stop-loss price (the Exit Condition `priceBelow` field). */
  stopLoss?: string
}

/**
 * Builds reduce-only exit legs (take-profit + stop-loss) to attach to an entry
 * order, mirroring the chart bracket pattern in use-percept-trading.ts.
 *
 * - **TP** → a resting reduce-only limit on the opposite side. No trigger: a
 *   take-profit always sits in the favourable direction, so the limit rests
 *   until price reaches it (a sell above market / a buy below market).
 * - **SL** → a reduce-only limit on the opposite side guarded by a price
 *   trigger, so it doesn't fill immediately as a marketable limit. The trigger
 *   direction follows the entry side: `priceBelow` for a long, `priceAbove`
 *   for a short.
 *
 * `entry` supplies account/symbol/quantity/denomination; only the fields that
 * differ for an exit leg are overridden. Returns `[]` when no exit price is set.
 */
export function buildExitBracketLegs(
  entry: NewOrderParams,
  entrySide: 'buy' | 'sell',
  exit: ExitBracketConfig
): NewOrderParams[] {
  const exitSide = entrySide === 'buy' ? OrderSide.ORDER_SIDE_SELL : OrderSide.ORDER_SIDE_BUY

  // Exit legs are always absolute-price reduce-only limits — clear anything from
  // the entry that would conflict (relative pricing, post-only, market type).
  const leg = (price: string, triggerCondition?: NewOrderParams['triggerCondition']) => ({
    ...entry,
    orderSide: exitSide,
    orderType: OrderType.ORDER_TYPE_LIMIT,
    price,
    relativePrice: undefined,
    postOnly: undefined,
    reduceOnly: true,
    triggerCondition,
  })

  const legs: NewOrderParams[] = []

  const tp = exit.takeProfit?.trim()
  if (tp) legs.push(leg(tp))

  const sl = exit.stopLoss?.trim()
  if (sl) {
    legs.push(leg(sl, entrySide === 'buy' ? { priceBelow: sl } : { priceAbove: sl }))
  }

  return legs
}
